import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Notification,
  useNotify,
  SelectInput,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  avatar: {
    margin: "1em",
    display: "flex",
    justifyContent: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  search: {
    padding: "10px",
    margin: "16px",
  },
  inputContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  select: {
    paddingLeft: "0.5em",
    minWidth: "120px",
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
  />
);

interface FormValues {
  first_name?: string;
  last_name?: string;
  first_name_exact_search?: string;
  last_name_exact_search?: string;
}

const { Form } = withTypes<FormValues>();

const DupeAccountCheck = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);

  const handleSubmit = (inputData: any) => {
    setLoading(true);
    setSearchResults([]);
    notify("Searching for users...");

    const { first_name, last_name, first_name_exact_search, last_name_exact_search } = inputData;

    if (!first_name && !last_name) {
      notify("Please enter at least first name or last name", "error", "", undefined, 5000);
      setLoading(false);
      return;
    }

    let config = getConfigGet({
      first_name: first_name || '',
      last_name: last_name || '',
      first_name_exact_search: first_name_exact_search || 'true',
      last_name_exact_search: last_name_exact_search || 'true'
    });

    axios
      .get(ENDPOINTS.DUPE_ACCOUNT_CHECK, config)
      .then((res: any) => {
        setLoading(false);
        setSearchResults(res?.data?.data || []);
        notify(`Found ${res?.data?.data?.length-1 || 0} users`, "info", "", undefined, 5000);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to search for users",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.first_name && !values.last_name) {
      errors.first_name = "At least first name or last name is required";
    }
    return errors;
  };

  const handleEmailSwap = (email1: string, email2: string) => {
    if (!email1 || !email2) {
      notify("Please enter both email addresses", "error", "", undefined, 5000);
      return;
    }

    if (email1 === email2) {
      notify("Both email addresses are the same", "error", "", undefined, 5000);
      return;
    }

    setSwapLoading(true);
    notify("Swapping email addresses...");

    const data = {
      email1: email1,
      email2: email2
    };

    const config = {
      headers: getConfigPost()
    };

    axios
      .post(ENDPOINTS.DUPE_ACCOUNT_CHECK, data, config)
      .then((res: any) => {
        setSwapLoading(false);
        notify("Email addresses swapped successfully", "info", "", undefined, 5000);
      })
      .catch((error) => {
        setSwapLoading(false);
        notify(
          error?.response?.data?.message || "Failed to swap email addresses",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  const searchTypeOptions = [
    { id: 'true', name: 'Exact Match' },
    { id: 'false', name: 'Partial Match' }
  ];

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <div>
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.main}>
                <div style={{ 
                  padding: "20px", 
                  border: "1px solid #e0e0e0", 
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  marginBottom: "20px"
                }}>
                  <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>Search Users</h3>
                  <div className={classes.form}>
                  <div className={classes.inputContainer}>
                    <Field
                      name="first_name"
                      component={renderInput}
                      label="First Name"
                      disabled={loading}
                    />
                    <SelectInput
                      className={classes.select}
                      source="first_name_exact_search"
                      choices={searchTypeOptions}
                      label="Search Type"
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <Field
                      name="last_name"
                      component={renderInput}
                      label="Last Name"
                      disabled={loading}
                    />
                    <SelectInput
                      className={classes.select}
                      source="last_name_exact_search"
                      choices={searchTypeOptions}
                      label="Search Type"
                    />
                  </div>
                  </div>
                  <CardActions className={classes.actions}>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ marginRight: '10px', flex: 1 }}
                    >
                      {loading && <CircularProgress size={25} thickness={2} />}
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setSearchResults([]);
                        // Reset form fields using react-final-form
                        form.reset();
                      }}
                      style={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </CardActions>
                </div>
                <Notification />
              </div>
            </form>
            <SearchResults results={searchResults} />
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <EmailSwapSection onEmailSwap={handleEmailSwap} loading={swapLoading} />
            </div>
          </div>
        )}
      />
    </>
  );
};

const EmailSwapSection = ({ onEmailSwap, loading }: any) => {
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');

  const handleSwap = () => {
    onEmailSwap(email1, email2);
  };

  return (
    <div style={{ 
      marginTop: "30px", 
      padding: "20px", 
      border: "1px solid #e0e0e0", 
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
      display: "inline-block",
      width: "auto"
    }}>
      <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#333" }}>Email Swap</h3>
      <div style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "center" }}>
        <TextField
          label="Email 1"
          value={email1}
          onChange={(e) => setEmail1(e.target.value)}
          disabled={loading}
          style={{ width: "250px" }}
        />
        <TextField
          label="Email 2"
          value={email2}
          onChange={(e) => setEmail2(e.target.value)}
          disabled={loading}
          style={{ width: "250px" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSwap}
          disabled={loading || !email1 || !email2}
          style={{ minWidth: "120px" }}
        >
          {loading && <CircularProgress size={20} thickness={2} style={{ marginRight: "8px" }} />}
          Swap Emails
        </Button>
      </div>
    </div>
  );
};

const SearchResults = ({ results }: any) => {
  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      marginTop: "20px",
      width: "100%"
    }}>
      <ShowData record={results} tableCenterAlign={true} />
    </div>
  );
};

export default DupeAccountCheck;
